import { UserService } from './../../../../core/services/user.service';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import * as moment from 'moment';
import { Subscription, combineLatest, switchMap, take, takeUntil, firstValueFrom } from 'rxjs';
import { PatientSelectionDialogComponent } from 'src/app/shared/components/patient-selection-dialog/patient-selection-dialog.component';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { AppointmentTypeService } from './../../../../core/services/appointment-type.service';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { PatientAppointmentService } from './../../../../core/services/patient-appointment.service';
import { PatientService } from './../../../../core/services/patient.service';
import { ScheduleService } from './../../../../core/services/schedule.service';
import { GroupService } from './../../../../core/services/group.service';
import { CalendarPhysicianFilterService } from './../../../../core/services/calendar-physician-filter.service';
import { CalendarLocationFilterService } from './../../../../core/services/calendar-location-filter.service';
import { CalendarConfirmFilterService } from './../../../../core/services/calendar-confirm-filter.service';
import { canUnconfirm, confirmationTooltip, needsConfirming, statusUpdateBody } from 'src/app/core/utils/appointment-confirmation';
import { LocationService } from './../../../../core/services/location.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { applyAppointmentStatus, reoccupiesSlot, shouldWarnOfConflict } from 'src/app/core/utils/slot-capacity';
import { editEventExtras, eventDayEntries } from 'src/app/core/utils/schedule-events';
import { otherPracticeName, practiceOf } from 'src/app/core/utils/calendar-practices';
import { PracticeSwitchService } from 'src/app/core/services/practice-switch.service';

interface Patient {
  id: string;
  surname: string;
  name: string;
  patientNumber?: string;
  diagnosis?: string;
  diagnosis2?: string;
  requiresValidation: boolean;
  reasonForVisiting?: string;
}

interface Appointment {
  Patient?: Patient;
  reason?: string;
  diagnosis?: string;
  diagnosis2?: string;
  procedure?: string;
  reasonForVisit?: string;
  typeDetails: {
    type: string;
    backgroundColor: string;
    textColor: string;
    borderColor?: string;
  };
  isAllDay?: boolean;
  date: string;
  timeSlot: string;
  endSlot?: string;
  appointmentType?: string;
  virtualLink?: string;
  patientArrivedTime?: string;
  endTime?: string;
  startTime?: string;
  noteSaved?: boolean;
  /** Set when the row belongs to one of the doctor's other practices. */
  practiceName?: string | null;
  isCancelled?: boolean | null;
  didNotAttend?: boolean | null;
  confirmedAt?: string | null;
  confirmedByName?: string | null;
}

interface AppointmentGroup {
  date: string;
  appointments: Appointment[];
}

@Component({
  selector: 'app-schedule-list',
  templateUrl: './schedule-list.component.html',
  styleUrls: ['./schedule-list.component.scss'],
})
export class ScheduleListComponent extends BaseComponent implements OnInit {

  appointments: AppointmentGroup[] | null = null;
  appointmentTypes: string[] = [];
  isDoctor?: boolean;

  calendarPhysicianFilterSelectValue: string = 'combined';
  doctorUsersForCalendar: { userId: string; title?: string; name?: string; surname?: string }[] = [];
  /** The group's locations; the location switcher shows only when there are some. */
  calendarLocations: any[] = [];
  /** null = all locations. */
  calendarLocationFilterId: string | null = null;

  /** Synced from `selectedGroup$` so the physician switcher and filter stay in sync with group changes. */
  calendarSplitByPhysician = false;

  displayedGroupsCount = 5;
  batchSize = 5;

  private patientsSubscription?: Subscription;

  constructor(
    private userService: UserService,
    private patientAppointmentService: PatientAppointmentService,
    private scheduleService: ScheduleService,
    private patientService: PatientService,
    private appointmentTypeService: AppointmentTypeService,
    public groupService: GroupService,
    private calendarPhysicianFilter: CalendarPhysicianFilterService,
    private calendarLocationFilter: CalendarLocationFilterService,
    public calendarConfirmFilter: CalendarConfirmFilterService,
    private locationService: LocationService,
    private router: Router,
    private cd: ChangeDetectorRef,
    private snack: MatSnackBar,
    private practiceSwitch: PracticeSwitchService,
    dialog: MatDialog,
    fs: FeedbackService) {
    super(fs, dialog);
  }

  /** The user's practices, to name rows from practices other than the selected one. */
  practices: any[] = [];

  ngOnInit(): void {
    this.groupService.userGroups$
      .pipe(takeUntil(this.destroy$))
      .subscribe((groups: any[]) => {
        this.practices = groups || [];
      });

    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe((g) => {
        this.calendarSplitByPhysician = !!g?.calendarSplitByPhysician;
        this.cd.markForCheck();
      });

    this.groupService.groupUsers$
      .pipe(takeUntil(this.destroy$))
      .subscribe((users: any[]) => {
        this.doctorUsersForCalendar = (users || []).filter(
          (u: any) => u.userType === 'doctor' && u.userId !== '511f39e1-ff14-4a6d-b35a-6fcc22923b50',
        );
        this.cd.markForCheck();
      });

    this.calendarPhysicianFilter.selectedPhysicianId$
      .pipe(takeUntil(this.destroy$))
      .subscribe((id) => {
        this.calendarPhysicianFilterSelectValue = id === null ? 'combined' : id;
        this.cd.markForCheck();
      });

    this.locationService.locations$
      .pipe(takeUntil(this.destroy$))
      .subscribe((locations) => {
        this.calendarLocations = locations || [];
        this.cd.markForCheck();
      });

    this.calendarLocationFilter.selectedLocationId$
      .pipe(takeUntil(this.destroy$))
      .subscribe((id) => {
        this.calendarLocationFilterId = id;
        this.cd.markForCheck();
      });

    combineLatest([
      this.patientAppointmentService.upcomingAppointments$,
      this.scheduleService.upcomingEvents$,
      this.appointmentTypeService.appointmentTypes$,
      this.groupService.selectedGroup$,
      this.calendarPhysicianFilter.selectedPhysicianId$,
      this.calendarLocationFilter.selectedLocationId$,
      this.calendarConfirmFilter.unconfirmedOnly$,
    ]).pipe(
      takeUntil(this.destroy$)
    ).subscribe(([appointments, events, appointmentTypes, _group, physicianFilter, locationFilter, unconfirmedOnly]) => {

      if (appointmentTypes) {
        this.appointmentTypes = appointmentTypes;
      }

      if (appointments || events) {
        const filteredAppts = this.filterAppointments(appointments || [], physicianFilter, locationFilter);
        // "Not yet confirmed" is a call list: only bookings still to confirm, no events.
        this.appointments = this.groupAndSortAppointments(unconfirmedOnly
          ? this.calendarConfirmFilter.applyAppointmentFilter(filteredAppts, true)
          : [...filteredAppts, ...(events || [])]);

        this.displayedGroupsCount = this.batchSize;
        this.cd.detectChanges();
      }
    });

    this.userService.userProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.isDoctor = result?.userType === 'doctor'
      });
  }

  get calendarPhysicianFilterButtonLabel(): string {
    if (this.calendarPhysicianFilterSelectValue === 'combined') {
      return 'Combined';
    }
    const u = this.doctorUsersForCalendar.find(
      (d) => d.userId === this.calendarPhysicianFilterSelectValue,
    );
    if (!u) {
      return 'Physician';
    }
    const parts = [u.title, u.name, u.surname].filter(Boolean);
    return parts.join(' ').trim() || 'Physician';
  }

  get calendarLocationFilterButtonLabel(): string {
    return this.calendarLocations.find((l) => l.id === this.calendarLocationFilterId)?.name || 'All locations';
  }

  selectLocationFilterFromMenu(locationId: string | null): void {
    this.calendarLocationFilter.setSelectedLocationId(locationId);
  }

  selectPhysicianFilterFromMenu(physicianId: string | null): void {
    this.calendarPhysicianFilter.setSelectedPhysicianId(physicianId);
  }

  onScroll(e: Event) {
    const el = (e.target as HTMLElement);
    if (el.scrollHeight - el.scrollTop <= el.clientHeight + 10) {
      if (this.appointments && this.displayedGroupsCount < this.appointments.length) {
        this.displayedGroupsCount = Math.min(
          this.appointments.length,
          this.displayedGroupsCount + this.batchSize
        );
      }
      if (this.patientAppointmentService.hasMoreUpcomingAppointments()) {
        this.patientAppointmentService.loadMoreUpcomingAppointments().subscribe();
      }
      if (this.scheduleService.hasMoreUpcomingEvents()) {
        this.scheduleService.loadMoreUpcomingEvents().subscribe();
      }
    }
  }

  buildDiagnosisString(patient: any): string {
    const { diagnosis2, diagnosis3, diagnosis4, diagnosis5 } = patient;
    const diagnoses = [diagnosis2, diagnosis3, diagnosis4, diagnosis5].filter(Boolean); // Filter out falsy values
    const diagnosisString = diagnoses.join(', ');
    return diagnosisString.length > 70 ? `${diagnosisString.substring(0, 70)}...` : diagnosisString;
  }

  private groupAndSortAppointments(appointments: any[] | null): any[] | null {
    if (!appointments?.length) {
      return appointments;
    }

    // One entry per day the event holds, in the event's own colours.
    const generateAppointmentsForRange = (event: any) => eventDayEntries(event).map(entry => ({
      ...entry,
      typeDetails: {
        type: event.reason,
        backgroundColor: event.backgroundColor ?? '#424242',
        textColor: event.textColor ?? '#fff',
        borderColor: event.backgroundColor ?? '#4caf50'
      }
    }));

    return _.chain(appointments)
      .flatMap(appointment => {
        if (appointment.date) {
          // Handle appointments with a date property
          // const typeDetails = this.appointmentTypes.find((type: any) => type.type === appointment.appointmentType); 

          let typeDetails;
          typeDetails = this.appointmentTypes.find((type: any) => type.id === appointment.appointmentTypeId) ?? undefined;
          if (!typeDetails) {
            typeDetails = this.appointmentTypes.find((type: any) => type.type === appointment.appointmentType) ?? undefined;
          }
          return {
            ...appointment,
            typeDetails: typeDetails ?? null // Default if not found
          };
        } else if (appointment.startDate && appointment.endDate) {
          // Handle appointments with startDate and endDate properties
          return generateAppointmentsForRange(appointment);
        } else {
          return appointment;
        }
      })
      .groupBy(appointment => moment(appointment.date).format('YYYY-MM-DD'))
      .map((value, key) => ({
        date: key,
        appointments: _.chain(value)
          .map(appointment => {
            let typeDetails;
            typeDetails = appointment.isAllDay ? appointment.typeDetails : this.appointmentTypes.find((type: any) => type.id === appointment.appointmentTypeId) ?? undefined;
            if (!typeDetails) {
              typeDetails = this.appointmentTypes.find((type: any) => type.type === appointment.appointmentType) ?? undefined;
            }

            return {
              ...appointment,
              typeDetails: typeDetails || {
                type: appointment.appointmentType,
                backgroundColor: '#424242',
                textColor: '#000',
                borderColor: '#4caf50'
              } // Default if not found
            }
          })
          .sortBy(appointment => appointment.timeSlot)
          .value()
      }))
      .orderBy(group => moment(group.date, 'YYYY-MM-DD'), 'asc')
      .value();
  }

  shouldShowArrivalOptions(appointment: any): boolean {
    if (this.isDoctor) return false;
    return moment(appointment.date).isSame(moment(), 'day');
  }

  // New methods to control which menu options are shown based on date
  shouldShowArrivedOption(appointment: any): boolean {
    // Only show arrived option for today's appointments
    return !appointment.patientArrivedTime &&
      appointment.isCancelled !== true &&
      appointment.didNotAttend !== true &&
      moment(appointment.date).isSame(moment(), 'day');
  }

  shouldShowNotArrivedOption(appointment: any): boolean {
    // Only show not arrived option for today's appointments that have arrived
    return appointment.patientArrivedTime &&
      !appointment.startTime &&
      !appointment.endTime &&
      moment(appointment.date).isSame(moment(), 'day');
  }

  shouldShowCancelledOption(appointment: any): boolean {
    // Show cancelled option for all dates except when already cancelled
    return appointment.isCancelled !== true;
  }

  shouldShowNotCancelledOption(appointment: any): boolean {
    // Show not cancelled option when currently cancelled
    return appointment.isCancelled === true;
  }

  shouldShowDidNotAttendOption(appointment: any): boolean {
    // Show did not attend option for past dates and today, but not future dates
    return appointment.didNotAttend !== true &&
      appointment.isCancelled !== true &&
      moment(appointment.date).isSameOrBefore(moment(), 'day');
  }

  shouldShowDidAttendOption(appointment: any): boolean {
    // Show did attend option when currently marked as did not attend
    return appointment.didNotAttend === true;
  }

  /**
   * This practice's rows under its doctor and location filters. Rows from the doctor's other
   * practices are named and left out of those filters.
   */
  filterAppointments(appointments: any[], physicianFilter: string | null, locationFilter: string | null): any[] {
    const named = appointments.map(a => ({ ...a, practiceName: otherPracticeName(a, this.groupService?.group?.id, this.practices) }));
    const own = named.filter(a => !a.practiceName);
    return [
      ...(this.calendarLocationFilter.applyAppointmentFilter(
        this.calendarPhysicianFilter.applyAppointmentFilter(own, this.calendarSplitByPhysician, physicianFilter),
        locationFilter,
      ) ?? []),
      ...named.filter(a => a.practiceName),
    ];
  }

  /** Opens a row's patient; another practice's row switches to that practice first. */
  async openBooking(appointment: any): Promise<void> {
    if (!appointment?.Patient) return;
    if (appointment.practiceName) {
      const practice = practiceOf(appointment, this.practices);
      if (!practice || !(await this.practiceSwitch.switchTo(practice))) return;
    }
    this.navigateToPatientFile(appointment.Patient.id);
  }

  shouldShowStatusMenu(appointment: any): boolean {
    if (this.isDoctor || appointment.practiceName) return false;
    // Today and past bookings, and upcoming ones to confirm or cancel before the day.
    return moment(appointment.date).isSameOrBefore(moment(), 'day') || !!appointment.patientId;
  }

  readonly needsConfirming = needsConfirming;
  readonly canUnconfirm = canUnconfirm;
  readonly confirmationTooltip = confirmationTooltip;

  setUnconfirmedOnly(on: boolean): void {
    this.calendarConfirmFilter.setUnconfirmedOnly(on);
  }

  // Method to check if appointment is older than 24 hours
  isAppointmentOlderThan24Hours(appointment: any): boolean {
    if (!appointment.date) return false;
    const appointmentDate = moment(appointment.date);
    const now = moment();
    return now.diff(appointmentDate, 'hours') > 24;
  }

  // Method to check if doctor should see the completion menu
  shouldShowDoctorCompletionMenu(appointment: any): boolean {
    return this.isDoctor === true && !appointment.practiceName &&
      this.isAppointmentOlderThan24Hours(appointment) &&
      appointment.noteSaved !== true;
  }

  // Method to mark note as saved
  markNoteAsSaved(appointment: any, event: MouseEvent) {
    event.stopPropagation();

    if (this.patientService.isRecordingLocked()) {
      return;
    }

    // Set the noteSaved flag to true
    appointment.noteSaved = true;

    const sub: Subscription = this.patientAppointmentService.updateAppointment(appointment.id, appointment)
      .subscribe({
        next: (result) => {
          this.showToastMessage('Note marked as saved');
          sub.unsubscribe();
        },
        error: (error) => {
          console.error('Error updating appointment:', error);
          this.showToastMessage('There was an error updating the appointment. Please try again.');
        }
      });
  }

  /*   togglePatientArrived(event: MouseEvent, appointment: any, clearValue?: boolean) {
      event.stopPropagation();
      appointment.patientArrivedTime = clearValue ? null : moment().toISOString();
      const sub: Subscription = this.patientAppointmentService.updateAppointment(appointment.id, appointment).subscribe(result => sub.unsubscribe())
    } */

  // New methods for expanded appointment status options
  async updateAppointmentStatus(appointment: any, status: string, event: MouseEvent) {
    event.stopPropagation();

    const next = applyAppointmentStatus(appointment, status);
    if (reoccupiesSlot(appointment, next)) {
      const conflicts = await firstValueFrom(
        this.patientAppointmentService.findSlotConflictsFor(next, this.calendarSplitByPhysician));
      if (shouldWarnOfConflict(conflicts, this.scheduleService.currentSchedule)) {
        const proceed = await this.showConfirmationDialog('SCHEDULING CONFLICT', 'Appointments/Events already exist for this slot. Do you want to continue anyway?', conflicts);
        if (!proceed) return;
      }
    }
    Object.assign(appointment, next);

    const sub: Subscription = this.patientAppointmentService.updateAppointment(appointment.id, statusUpdateBody(appointment, status)).subscribe(result => sub.unsubscribe());
  }



  addEvent() {
    if (this.patientService.isRecordingLocked()) {
      return;
    }
    this.router.navigate(['/patients', {
      outlets: {
        patient: ['add-event']
      }
    }]);
  }

  openScheduleDialog(): void {
    if (this.patientService.isRecordingLocked()) {
      return;
    }

    if (this.patientsSubscription) {
      this.patientsSubscription.unsubscribe();
    }

    this.patientsSubscription = this.patientService.patients$
      .pipe(
        take(1),
        switchMap(patients => {
          const dialogRef = this.dialog.open(PatientSelectionDialogComponent, {
            width: '400px',
            data: { patients }
          });
          return dialogRef.afterClosed();
        })
      )
      .subscribe(selectedPatient => {
        if (!selectedPatient) return;

        switch (selectedPatient) {
          case 'event':
            this.router.navigate(['/patients', {
              outlets: {
                patient: ['add-event']
              }
            }]);
            break;
          case 'opd':
          case 'invite':
            this.patientService.getPatientById(null)
              .pipe(takeUntil(this.destroy$))
              .subscribe(result => {
                this.router.navigate(['/patients', {
                  outlets: {
                    patient: ['add'],
                  }
                }], { queryParams: { type: selectedPatient, redirectTo: 'add-appointment' } });
              }, error => {
                console.error(error);
                this.showToastMessage('There was an error creating the patient. Please try again.')
              });
            break;
          default:
            this.router.navigate(['/patients', {
              outlets: {
                patient: ['add-appointment']
              }
            }], {
              queryParams: {
                patientId: selectedPatient.id,
              }
            });
            break;
        }
      });
  }

  navigateToPatientFile(patientId: string) {
    this.patientService.getPatientById(patientId)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.router.navigate(['/patients', {
          outlets: {
            patient: ['file', patientId],
          }
        }]);
      }, error => {
        console.error(error);
        this.showToastMessage('There was an error loading the patient file. Please try again.');
      });
  }

  rescheduleAppointment(appointment: any, event: MouseEvent): void {
    event.stopPropagation();
    if (this.patientService.isRecordingLocked()) {
      return;
    }
    if (appointment.reason) {
      this.router.navigate(['/patients', {
        outlets: {
          patient: ['edit-event', appointment.id],
        }
      }], editEventExtras(appointment));
      return;
    }

    this.router.navigate(['/patients', {
      outlets: {
        patient: ['edit-appointment', appointment.id],
      }
    }]);
  }

  private openLinkInNewTab(link: string): void {
    // Try multiple methods to open the link and avoid popup blocker
    try {
      // Method 1: Create a temporary link element and trigger click
      const tempLink = document.createElement('a');
      tempLink.href = link;
      tempLink.target = '_blank';
      tempLink.rel = 'noopener noreferrer';
      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);
    } catch (error) {
      console.warn('Failed to open link with temporary element method:', error);
      try {
        // Method 2: Fallback to window.open
        const newWindow = window.open(link, '_blank');
        if (!newWindow) {
          console.warn('Popup blocked by browser');
          // Method 3: Show user a message to manually open the link
          this.showToastMessage('Please manually open the virtual consultation link: ' + link);
        }
      } catch (fallbackError) {
        console.error('All methods to open link failed:', fallbackError);
        this.showToastMessage('Please manually open the virtual consultation link: ' + link);
      }
    }
  }

  async loadVirtualLink(appointment: any, event: MouseEvent) {
    if (this.patientService.isRecordingLocked()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    let link = appointment.virtualLink.trim();
    // Ensure the link has a protocol
    if (!link.match(/^https?:\/\//)) {
      link = 'http://' + link;
    }

    // If no patient associated with appointment, just open the link
    if (!appointment.Patient) {
      console.log('No patient associated with appointment, opening link directly');
      this.openLinkInNewTab(link);
      return;
    }

    // Ask user if they want to record
    const startRecording = await this.feedbackService.showConfirmation(
      'Start Recording?',
      'You are about to enter a virtual consultation link. Would you like to start an AI-generated recording for the consultation? Remember to return later to stop the recording once you\'re done.',
      [],
      'Go To Meeting Only',
      'Go To Meeting + Record'
    );

    if (startRecording) {
      // Navigate to patient file for recording first
      const patientId = appointment.Patient.id;

      // Clear current patient and navigate to patient file
      this.patientService.setCurrentPatient(null);
      this.router.navigate(['/patients', { outlets: { patient: null } }]);

      // Load patient and navigate to file view
      this.patientService.getPatientById(patientId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            console.log('Patient loaded successfully, navigating to file view');
            // Navigate to the patient's file view
            this.router.navigate(['/patients', {
              outlets: { patient: ['file', patientId] }
            }]);

            // Wait for navigation to complete, then trigger AI recording
            setTimeout(() => {
              console.log('Triggering AI recording');
              this.patientService.triggerAiRecording.next(true);
              this.showToastMessage('AI recording is starting. Virtual link will open in 2 seconds...');

              // Open link after a very short delay to let user see recording start
              setTimeout(() => {
                console.log('Opening virtual link in new tab');
                this.openLinkInNewTab(link);
              }, 2000); // 2 second delay - short enough to avoid popup blockers
            }, 1000);
          },
          error: error => {
            console.error('Error loading patient file:', error);
            this.showToastMessage('There was an error loading the patient file. Please try again.');
            // Still open the link even if patient file fails to load
            this.openLinkInNewTab(link);
          }
        });
    } else {
      console.log('User chose not to record, opening link directly');
      // Just open the link without recording
      this.openLinkInNewTab(link);
    }
  }
}
